import { AdminLayout } from '@/components/layouts'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Link, router } from '@inertiajs/react'
import {
  Search,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  Eye,
  Edit,
  Trash2,
  MessageSquare,
  ThumbsUp,
  MessageCircle,
  HelpCircle,
  Megaphone
} from 'lucide-react'
import { useState, useCallback } from 'react'

interface AdminPost {
  id: number
  title: string
  excerpt: string
  post_type: 'question' | 'discussion' | 'announcement'
  author_name: string
  community_name: string | null
  category_name: string | null
  likes_count: number
  comments_count: number
  views_count: number
  created_at: string
  updated_at: string
}

interface Pagination {
  current_page: number
  per_page: number
  total_count: number
  total_pages: number
}

interface Filters {
  search: string | null
  community_id: string | null
  post_type: string | null
  sort: string | null
  direction: string | null
}

interface Community {
  id: number
  name: string
}

interface PostType {
  value: string
  label: string
}

interface IndexProps {
  posts: AdminPost[]
  pagination: Pagination
  filters: Filters
  communities: Community[]
  post_types: PostType[]
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

const POST_TYPE_CONFIG = {
  question: {
    icon: HelpCircle,
    label: 'Question',
    className: 'bg-blue-100 text-blue-800'
  },
  discussion: {
    icon: MessageSquare,
    label: 'Discussion',
    className: 'bg-gray-100 text-gray-700'
  },
  announcement: {
    icon: Megaphone,
    label: 'Annonce',
    className: 'bg-yellow-100 text-yellow-800'
  }
}

export default function Index({ posts, pagination, filters, communities, post_types }: IndexProps) {
  const [search, setSearch] = useState(filters.search || '')

  const applyFilters = useCallback((newFilters: Partial<Filters>) => {
    router.get('/admin/posts', {
      ...filters,
      ...newFilters,
      page: 1
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }, [filters])

  const handleSearch = useCallback(() => {
    applyFilters({ search: search || null })
  }, [search, applyFilters])

  const handleSort = useCallback((field: string) => {
    const newDirection = filters.sort === field && filters.direction === 'desc' ? 'asc' : 'desc'
    applyFilters({ sort: field, direction: newDirection })
  }, [filters, applyFilters])

  const handlePageChange = useCallback((page: number) => {
    router.get('/admin/posts', {
      ...filters,
      page
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }, [filters])

  const handleDelete = useCallback((post: AdminPost) => {
    if (confirm(`Voulez-vous vraiment supprimer la publication "${post.title}" ?`)) {
      router.delete(`/admin/posts/${post.id}`)
    }
  }, [])

  return (
    <AdminLayout
      title="Publications"
      breadcrumbs={[{ label: 'Publications' }]}
    >
      {/* Actions Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4 mb-6">
        <div className="flex flex-1 gap-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="Rechercher par titre, contenu, auteur..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch} variant="secondary">
                <Search size={18} />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="w-48">
            <Select
              value={filters.community_id || ''}
              onChange={(e) => applyFilters({ community_id: e.target.value || null })}
            >
              <option value="">Toutes communautes</option>
              {communities.map((community) => (
                <option key={community.id} value={community.id}>
                  {community.name}
                </option>
              ))}
            </Select>
          </div>

          <div className="w-40">
            <Select
              value={filters.post_type || ''}
              onChange={(e) => applyFilters({ post_type: e.target.value || null })}
            >
              <option value="">Tous types</option>
              {post_types.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Select>
          </div>

          {(filters.search || filters.community_id || filters.post_type) && (
            <Button
              variant="secondary"
              onClick={() => {
                setSearch('')
                applyFilters({
                  search: null,
                  community_id: null,
                  post_type: null
                })
              }}
            >
              Reinitialiser
            </Button>
          )}
        </div>
      </Card>

      {/* Results Info */}
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-text-gray">
          {pagination.total_count} publication{pagination.total_count > 1 ? 's' : ''}
          {pagination.total_pages > 1 && (
            <span> - Page {pagination.current_page} sur {pagination.total_pages}</span>
          )}
        </p>
      </div>

      {/* Posts Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3">
                  <button
                    className="flex items-center gap-1 font-medium text-text-gray hover:text-text-dark"
                    onClick={() => handleSort('title')}
                  >
                    Publication
                    <ArrowUpDown size={14} />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">
                  Type
                </th>
                <th className="text-left px-4 py-3">
                  <button
                    className="flex items-center gap-1 font-medium text-text-gray hover:text-text-dark"
                    onClick={() => handleSort('author')}
                  >
                    Auteur
                    <ArrowUpDown size={14} />
                  </button>
                </th>
                <th className="text-left px-4 py-3">
                  <button
                    className="flex items-center gap-1 font-medium text-text-gray hover:text-text-dark"
                    onClick={() => handleSort('likes_count')}
                  >
                    <ThumbsUp size={14} />
                    <ArrowUpDown size={14} />
                  </button>
                </th>
                <th className="text-left px-4 py-3">
                  <button
                    className="flex items-center gap-1 font-medium text-text-gray hover:text-text-dark"
                    onClick={() => handleSort('comments_count')}
                  >
                    <MessageCircle size={14} />
                    <ArrowUpDown size={14} />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">
                  Date
                </th>
                <th className="text-right px-4 py-3 font-medium text-text-gray">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {posts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-text-gray">
                    <div className="flex flex-col items-center gap-2">
                      <MessageSquare size={32} className="text-gray-300" />
                      Aucune publication trouvee
                    </div>
                  </td>
                </tr>
              ) : (
                posts.map((post) => {
                  const typeConfig = POST_TYPE_CONFIG[post.post_type]
                  const TypeIcon = typeConfig.icon
                  return (
                    <tr key={post.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="max-w-xs">
                          <p className="font-medium text-text-dark truncate">{post.title}</p>
                          <p className="text-xs text-text-gray truncate mt-0.5">{post.excerpt}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${typeConfig.className}`}>
                          <TypeIcon size={12} />
                          {typeConfig.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-text-dark">
                          {post.author_name}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-text-dark font-medium">
                          {post.likes_count}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-text-dark font-medium">
                          {post.comments_count}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-text-gray">
                        {formatDate(post.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Link href={`/admin/posts/${post.id}`}>
                            <Button variant="secondary" className="p-2">
                              <Eye size={16} />
                            </Button>
                          </Link>
                          <Link href={`/admin/posts/${post.id}/edit`}>
                            <Button variant="secondary" className="p-2">
                              <Edit size={16} />
                            </Button>
                          </Link>
                          <Button
                            variant="secondary"
                            className="p-2 text-red-600 hover:bg-red-50"
                            onClick={() => handleDelete(post)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <Button
              variant="secondary"
              disabled={pagination.current_page === 1}
              onClick={() => handlePageChange(pagination.current_page - 1)}
              className="flex items-center gap-1"
            >
              <ChevronLeft size={16} />
              Precedent
            </Button>

            <div className="flex items-center gap-2">
              {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                let pageNum: number
                if (pagination.total_pages <= 5) {
                  pageNum = i + 1
                } else if (pagination.current_page <= 3) {
                  pageNum = i + 1
                } else if (pagination.current_page >= pagination.total_pages - 2) {
                  pageNum = pagination.total_pages - 4 + i
                } else {
                  pageNum = pagination.current_page - 2 + i
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`w-8 h-8 rounded text-sm font-medium transition-colors ${
                      pagination.current_page === pageNum
                        ? 'bg-tessan-primary text-white'
                        : 'bg-white text-text-gray hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              })}
            </div>

            <Button
              variant="secondary"
              disabled={pagination.current_page === pagination.total_pages}
              onClick={() => handlePageChange(pagination.current_page + 1)}
              className="flex items-center gap-1"
            >
              Suivant
              <ChevronRight size={16} />
            </Button>
          </div>
        )}
      </Card>
    </AdminLayout>
  )
}
